import type { DropPodVariant, DropPodCollectible, SkyTheme } from "@/types/drop-pod.types"

// Sky theme for early morning - warm oranges and peaches
const goldenDawnTheme: SkyTheme = {
  spaceTop: "#1a0a1a",
  spaceBottom: "#2a1a2a",
  starColor: "#ffccaa",
  starCount: 80,
  atmosphereTop: "#4a2a3a",
  atmosphereBottom: "#8a4a3a",
  skyTop: "#ff9966",
  skyMiddle: "#ffcc99",
  skyBottom: "#ffe6cc",
  cloudColor: "#ffffff",
  cloudOpacity: 0.5,
}

// Sky theme for bright day - current default sky
const clearDayTheme: SkyTheme = {
  spaceTop: "#0a0a1a",
  spaceBottom: "#1a1a3a",
  starColor: "#ffffff",
  starCount: 100,
  atmosphereTop: "#1a3a6a",
  atmosphereBottom: "#3a5a8a",
  skyTop: "#3c6eb4",
  skyMiddle: "#87ceeb",
  skyBottom: "#b0e0e6",
  cloudColor: "#ffffff",
  cloudOpacity: 0.4,
}

// Sky theme for evening - purple and pink
const twilightRunTheme: SkyTheme = {
  spaceTop: "#0a0515",
  spaceBottom: "#1a0a2a",
  starColor: "#eeddff",
  starCount: 120,
  atmosphereTop: "#3a1a4a",
  atmosphereBottom: "#5a2a6a",
  skyTop: "#9966cc",
  skyMiddle: "#cc99cc",
  skyBottom: "#e6b3cc",
  cloudColor: "#ffccee",
  cloudOpacity: 0.3,
}

// Sky theme for dusk - deep orange and red sunset
const duskDescentTheme: SkyTheme = {
  spaceTop: "#1a0505",
  spaceBottom: "#2a0a0a",
  starColor: "#ffaa88",
  starCount: 60,
  atmosphereTop: "#4a1515",
  atmosphereBottom: "#6a2a1a",
  skyTop: "#ff6633",
  skyMiddle: "#ff9966",
  skyBottom: "#ffcc99",
  cloudColor: "#ffeecc",
  cloudOpacity: 0.4,
}

// Sky theme for foggy - gray and muted
const fogBankTheme: SkyTheme = {
  spaceTop: "#151515",
  spaceBottom: "#252525",
  starColor: "#888888",
  starCount: 30,
  atmosphereTop: "#353535",
  atmosphereBottom: "#454545",
  skyTop: "#606060",
  skyMiddle: "#808080",
  skyBottom: "#a0a0a0",
  cloudColor: "#cccccc",
  cloudOpacity: 0.6,
  fogColor: "#ffffff",
  fogOpacity: 0.2,
}

// Helper to create collectibles with unique IDs
let collectibleId = 0
const createCollectible = (
  type: "coin" | "life" | "premium-tower",
  value: number,
  x: number,
  spawnTime: number
): DropPodCollectible => ({
  id: `collectible-${collectibleId++}`,
  type,
  value,
  x,
  spawnTime,
})

// Variant 1: Golden Dawn - Easy, more gold, wide gentle curves
// Total: ~20 gold, ~4 lives, ~2 towers, 80-90% collectible
const goldenDawnCollectibles: DropPodCollectible[] = [
  // Early pickups - center path
  createCollectible("coin", 2, 0.5, 0.08),
  createCollectible("coin", 3, 0.45, 0.14),
  createCollectible("coin", 2, 0.55, 0.20),
  createCollectible("life", 1, 0.5, 0.26),
  // Gentle curve left
  createCollectible("coin", 3, 0.35, 0.32),
  createCollectible("coin", 2, 0.3, 0.38),
  createCollectible("premium-tower", 1, 0.25, 0.44),
  // Back to center
  createCollectible("coin", 2, 0.4, 0.50),
  createCollectible("life", 2, 0.5, 0.56),
  createCollectible("coin", 3, 0.55, 0.62),
  // Gentle curve right
  createCollectible("coin", 2, 0.65, 0.68),
  createCollectible("coin", 3, 0.7, 0.74),
  createCollectible("premium-tower", 1, 0.65, 0.80),
  // Final stretch
  createCollectible("life", 1, 0.55, 0.86),
  createCollectible("coin", 2, 0.5, 0.92),
]

// Variant 2: Clear Day - Easy-Medium, balanced, standard zigzag
// Total: ~15 gold, ~6 lives, ~2 towers, 75-85% collectible
const clearDayCollectibles: DropPodCollectible[] = [
  // Start center
  createCollectible("coin", 2, 0.5, 0.06),
  createCollectible("life", 1, 0.45, 0.12),
  // Zigzag left
  createCollectible("coin", 2, 0.35, 0.18),
  createCollectible("coin", 2, 0.25, 0.24),
  createCollectible("life", 1, 0.3, 0.30),
  // Zigzag right
  createCollectible("coin", 3, 0.5, 0.36),
  createCollectible("premium-tower", 1, 0.65, 0.42),
  createCollectible("life", 1, 0.7, 0.48),
  // Back left
  createCollectible("coin", 2, 0.55, 0.54),
  createCollectible("life", 2, 0.4, 0.60),
  createCollectible("coin", 2, 0.3, 0.66),
  // Final right sweep
  createCollectible("coin", 2, 0.45, 0.72),
  createCollectible("premium-tower", 1, 0.6, 0.78),
  createCollectible("life", 1, 0.7, 0.84),
  createCollectible("coin", 2, 0.55, 0.92),
]

// Variant 3: Twilight Run - Medium, more lives, split paths
// Total: ~12 gold, ~10 lives, ~2 towers, 60-75% collectible
const twilightRunCollectibles: DropPodCollectible[] = [
  // Start - must choose left or right path
  createCollectible("life", 1, 0.5, 0.06),
  createCollectible("coin", 2, 0.5, 0.12),
  // Split path - left has lives, right has gold
  createCollectible("life", 2, 0.25, 0.20),
  createCollectible("coin", 3, 0.75, 0.20),
  createCollectible("life", 1, 0.2, 0.28),
  createCollectible("coin", 2, 0.8, 0.28),
  // Converge center
  createCollectible("premium-tower", 1, 0.5, 0.36),
  createCollectible("life", 2, 0.45, 0.44),
  // Split again
  createCollectible("life", 1, 0.3, 0.52),
  createCollectible("coin", 2, 0.7, 0.52),
  createCollectible("life", 2, 0.25, 0.60),
  createCollectible("coin", 3, 0.75, 0.60),
  // Final section
  createCollectible("life", 1, 0.4, 0.70),
  createCollectible("premium-tower", 1, 0.6, 0.70),
  createCollectible("life", 1, 0.5, 0.80),
  createCollectible("coin", 2, 0.5, 0.90),
]

// Variant 4: Dusk Descent - Medium-Hard, more premium towers, tight weaving
// Total: ~10 gold, ~4 lives, ~4 towers, 60-75% collectible
const duskDescentCollectibles: DropPodCollectible[] = [
  // Quick start
  createCollectible("coin", 2, 0.5, 0.05),
  createCollectible("premium-tower", 1, 0.4, 0.11),
  // Tight weave left
  createCollectible("coin", 2, 0.3, 0.17),
  createCollectible("life", 1, 0.25, 0.23),
  createCollectible("premium-tower", 1, 0.2, 0.29),
  // Sharp turn right
  createCollectible("coin", 3, 0.5, 0.35),
  createCollectible("life", 1, 0.7, 0.41),
  createCollectible("premium-tower", 1, 0.75, 0.47),
  // Back left quickly
  createCollectible("coin", 2, 0.55, 0.53),
  createCollectible("life", 1, 0.35, 0.59),
  createCollectible("coin", 2, 0.25, 0.65),
  // Final weave
  createCollectible("premium-tower", 1, 0.45, 0.73),
  createCollectible("life", 1, 0.6, 0.81),
  createCollectible("coin", 2, 0.5, 0.90),
]

// Variant 5: Fog Bank - Hard, high rewards but can't get all, complex branching
// Total: ~25 gold, ~8 lives, ~5 towers, 40-60% collectible (impossible to get all)
const fogBankCollectibles: DropPodCollectible[] = [
  // Opening - two paths immediately
  createCollectible("coin", 3, 0.3, 0.06),
  createCollectible("coin", 3, 0.7, 0.06),
  createCollectible("life", 2, 0.25, 0.12),
  createCollectible("premium-tower", 1, 0.75, 0.12),
  // Three-way split
  createCollectible("coin", 5, 0.15, 0.20),
  createCollectible("life", 2, 0.5, 0.20),
  createCollectible("premium-tower", 1, 0.85, 0.20),
  // Impossible choice - all three at once
  createCollectible("premium-tower", 1, 0.2, 0.28),
  createCollectible("coin", 3, 0.5, 0.28),
  createCollectible("life", 2, 0.8, 0.28),
  // Brief convergence
  createCollectible("coin", 2, 0.45, 0.36),
  createCollectible("life", 1, 0.55, 0.36),
  // Split wide again
  createCollectible("premium-tower", 1, 0.15, 0.44),
  createCollectible("coin", 3, 0.85, 0.44),
  createCollectible("life", 2, 0.2, 0.52),
  createCollectible("coin", 5, 0.8, 0.52),
  // Another three-way
  createCollectible("coin", 2, 0.25, 0.62),
  createCollectible("premium-tower", 1, 0.5, 0.62),
  createCollectible("life", 1, 0.75, 0.62),
  // Final gauntlet - impossible to get all
  createCollectible("life", 2, 0.2, 0.74),
  createCollectible("coin", 3, 0.5, 0.74),
  createCollectible("premium-tower", 1, 0.8, 0.74),
  createCollectible("coin", 2, 0.35, 0.84),
  createCollectible("life", 1, 0.65, 0.84),
  createCollectible("coin", 2, 0.5, 0.94),
]

// Define all 5 variants
export const dropPodVariants: DropPodVariant[] = [
  {
    id: "golden-dawn",
    name: "Golden Dawn",
    skyTheme: goldenDawnTheme,
    collectibles: goldenDawnCollectibles,
    difficulty: "easy",
  },
  {
    id: "clear-day",
    name: "Clear Day",
    skyTheme: clearDayTheme,
    collectibles: clearDayCollectibles,
    difficulty: "easy",
  },
  {
    id: "twilight-run",
    name: "Twilight Run",
    skyTheme: twilightRunTheme,
    collectibles: twilightRunCollectibles,
    difficulty: "medium",
  },
  {
    id: "dusk-descent",
    name: "Dusk Descent",
    skyTheme: duskDescentTheme,
    collectibles: duskDescentCollectibles,
    difficulty: "medium",
  },
  {
    id: "fog-bank",
    name: "Fog Bank",
    skyTheme: fogBankTheme,
    collectibles: fogBankCollectibles,
    difficulty: "hard",
  },
]

// Get a random variant
export const getRandomDropPodVariant = (): DropPodVariant => {
  const index = Math.floor(Math.random() * dropPodVariants.length)
  return dropPodVariants[index]
}

// Get variant by ID (for testing/debugging)
export const getDropPodVariantById = (id: string): DropPodVariant | undefined => {
  return dropPodVariants.find((v) => v.id === id)
}
